import { Descriptions, Skeleton, Table, Typography } from 'antd'
import VSpace from '../../common/VSpace'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { boolToYN } from '../../../helpers/strings'
import { useInvoicingProgress } from './queries'
import { PerTenantStat } from './progressModel'

const { Title, Text } = Typography

const InvoicingProgress = () => {
  const { data, isLoading } = useInvoicingProgress()

  if (isLoading || !data) {
    return <Skeleton active />
  }

  return (
    <VSpace>
      <Title level={3}>Invoicing Progress</Title>
      <Text>Found {data.merchantCount} merchants and {data.invoiceCount} invoices.</Text>

      <Title level={4}>Tenant Counts by MerchantStatus + MerchantApplicationStatus</Title>
      <Table
        dataSource={data.merchantStatusCombos}
        rowKey={(_, index) => String(index)}
        pagination={false}
        size="small"
        columns={[
          { title: 'Merchant Status', dataIndex: 'merchantStatus' },
          { title: 'Application Status', dataIndex: 'merchantApplicationStatus' },
          { title: 'Tenant Count', dataIndex: 'tenantCount' },
        ]}
      />

      <Title level={4}>InvoicingSystem Selected by Tenants w/ Merchant Applications</Title>
      <Table
        dataSource={data.invoicingSystems}
        rowKey="invoicingSystem"
        pagination={false}
        size="small"
        columns={[
          { title: 'Invoicing System', dataIndex: 'invoicingSystem' },
          { title: 'Tenant Count', dataIndex: 'tenantCount' },
        ]}
      />

      <Title level={4}>Payment Settings Combinations</Title>
      <Text type="secondary">
        Show = show &lsquo;Authorize Payment on File&rsquo; option on invoice.
        Require = require &lsquo;Authorize Payment on File&rsquo; option on invoice.
      </Text>
      <Table
        dataSource={data.paymentSettingsCombos}
        rowKey={(_, index) => String(index)}
        pagination={false}
        size="small"
        columns={[
          { title: 'CC', dataIndex: 'defaultCreditCard', render: (v: boolean) => boolToYN(v) },
          { title: 'ACH', dataIndex: 'defaultAch', render: (v: boolean) => boolToYN(v) },
          { title: 'Show', dataIndex: 'defaultShowAuthorizePaymentOnFile', render: (v: boolean) => boolToYN(v) },
          { title: 'Require', dataIndex: 'defaultRequireAuthorizePaymentOnFile', render: (v: boolean) => boolToYN(v) },
          { title: 'Count', dataIndex: 'tenantCount' },
        ]}
      />

      <Title level={4}>Active Merchants Using QuickBooks Invoicing</Title>
      <Table
        dataSource={[...data.activeMerchantsUsingQuickBooks].sort((a, b) => a.tenantName.localeCompare(b.tenantName))}
        rowKey="tenantId"
        pagination={false}
        size="small"
        columns={[
          { title: 'Tenant ID', dataIndex: 'tenantId' },
          { title: 'Tenant Name', dataIndex: 'tenantName' },
        ]}
        footer={() => <Text strong>Total: {data.activeMerchantsUsingQuickBooks.length} tenants</Text>}
      />

      <Title level={4}>Per-Tenant Invoice Stats</Title>
      <Table
        dataSource={[...data.perTenantStats].sort((a, b) => a.tenantName.localeCompare(b.tenantName))}
        rowKey="tenantId"
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '250'] }}
        size="small"
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Tenant',
            dataIndex: 'tenantName',
            sorter: (a: PerTenantStat, b: PerTenantStat) => a.tenantName.localeCompare(b.tenantName),
            defaultSortOrder: 'ascend',
          },
          { title: 'Invoices', dataIndex: 'invoices', sorter: (a: PerTenantStat, b: PerTenantStat) => a.invoices - b.invoices },
          { title: 'Unpaid', dataIndex: 'unpaid', sorter: (a: PerTenantStat, b: PerTenantStat) => a.unpaid - b.unpaid },
          { title: 'Manual', dataIndex: 'manual', sorter: (a: PerTenantStat, b: PerTenantStat) => a.manual - b.manual },
          { title: 'Pending', dataIndex: 'pending', sorter: (a: PerTenantStat, b: PerTenantStat) => a.pending - b.pending },
          { title: 'Complete', dataIndex: 'complete', sorter: (a: PerTenantStat, b: PerTenantStat) => a.complete - b.complete },
          { title: 'Problems', dataIndex: 'problems', sorter: (a: PerTenantStat, b: PerTenantStat) => a.problems - b.problems },
          { title: 'Not Synced (Invoice)', dataIndex: 'notSyncedInvoice', sorter: (a: PerTenantStat, b: PerTenantStat) => a.notSyncedInvoice - b.notSyncedInvoice },
          { title: 'Not Synced (Payment)', dataIndex: 'notSyncedPayment', sorter: (a: PerTenantStat, b: PerTenantStat) => a.notSyncedPayment - b.notSyncedPayment },
        ]}
      />

      <Title level={4}>Aggregate Invoice Sums</Title>
      <Text type="secondary">Including sales tax.</Text>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Total">{centsToDollarsDisplay(data.aggregateSums.total)}</Descriptions.Item>
        <Descriptions.Item label="Pending">{centsToDollarsDisplay(data.aggregateSums.pending)}</Descriptions.Item>
        <Descriptions.Item label="Manual">{centsToDollarsDisplay(data.aggregateSums.manual)}</Descriptions.Item>
        <Descriptions.Item label="Completed">{centsToDollarsDisplay(data.aggregateSums.completed)}</Descriptions.Item>
      </Descriptions>
    </VSpace>
  )
}

export default InvoicingProgress
